import { preparePhoto } from "../components/admin/PhotoCropper";

/* Photos as they are uploaded, and the answer read back.

   A phone photo is 4–8MB, and room/property photos go up several to one
   request. nginx caps a request at 50MB (ops/nginx/tenant.template.conf),
   so a handful of photos was refused by nginx itself with an HTML 413 page
   — which the page then tried to read as JSON ("Unexpected token '<'").

   The server re-encodes every photo to at most 1600px anyway
   (services/imageOptimize.js), so shrinking to 2048px here first loses
   nothing that would have been kept, and takes a photo to well under 1MB.
   Same decode as the profile-photo cropper (preparePhoto), which is also
   what makes the preview displayable under the live CSP — a blob: URL is
   refused there, a data: URL is not. */

const MAX_SIDE = 2048;
// Per photo, as the server enforces it (backend/utils/uploadLimits.js).
export const PHOTO_MAX_MB = 20;

function dataUrlToFile(dataUrl, name) {
  const [head, body] = dataUrl.split(",");
  const type = head.match(/^data:([^;,]+)/)?.[1] || "application/octet-stream";
  const bin = atob(body);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const finalName = type === "image/jpeg" ? name.replace(/\.[^.]*$/, "") + ".jpg" : name;
  return new File([bytes], finalName, { type });
}

/**
 * Shrink one chosen photo for upload. Returns `{ file, preview }`: the file
 * to send (the smaller of the shrunk copy and the original) and a data: URL
 * the page may display. A photo the browser can't decode is sent unchanged.
 */
export async function shrinkPhoto(file) {
  try {
    const preview = await preparePhoto(file, MAX_SIDE);
    const shrunk = dataUrlToFile(preview, file.name || "photo.jpg");
    return { file: shrunk.size < file.size ? shrunk : file, preview, name: file.name };
  } catch {
    return { file, preview: "", name: file.name };
  }
}

/** shrinkPhoto for several, one at a time — ten full-size decodes at once can exhaust a phone's memory. */
export async function shrinkPhotos(files) {
  const out = [];
  for (const f of files) out.push(await shrinkPhoto(f));
  return out;
}

/**
 * The prepared photos still over the limit after shrinking (only a photo the
 * browser couldn't decode is sent at its original size), and the message
 * that names them — said before uploading rather than after a failed save.
 */
export function splitOversized(ready) {
  const limit = PHOTO_MAX_MB * 1024 * 1024;
  const ok = ready.filter((r) => r.file.size <= limit);
  const big = ready.filter((r) => r.file.size > limit);
  const message = big.length
    ? `${big.map((r) => `"${r.name}" (${(r.file.size / 1048576).toFixed(1)}MB)`).join(", ")} ${big.length === 1 ? "is" : "are"} too large — each photo must be under ${PHOTO_MAX_MB}MB.`
    : "";
  return { ok, big, message };
}

/**
 * The JSON body of an API response — or, when something in front of the
 * app (nginx) answered with an HTML error page, a readable error in the
 * same `{ success, error }` shape instead of a JSON parse failure.
 */
export async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    if (res.status === 413) {
      return { success: false, error: "These photos are too large to upload together. Try adding fewer at a time." };
    }
    return { success: false, error: `The server couldn't take that just now (error ${res.status}). Please try again.` };
  }
}
